import React from 'react';
import { Card } from '@/components/ui/card';
import { ArrowRight, Quote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReferenceProject } from '@/config/references';
import { getImageAssetPath } from '@/lib/imageHelper';
import { useTranslation } from '@/hooks/useTranslation';
import Gallery from './Gallery';

interface ReferenceCardProps {
  reference: ReferenceProject;
  isReversed: boolean;
}

const ReferenceCard = ({ reference, isReversed }: ReferenceCardProps) => {
  const { t, language } = useTranslation();
  
  // Logo-Pfad für die Anzeige neben dem Titel
  const logoPath = getImageAssetPath(`references/logo/${reference.logo}`);
  const descriptionText = reference.description ? reference.description[language as 'de' | 'en'] || reference.description.de : '';
  const challengeText = reference.challenge ? reference.challenge[language as 'de' | 'en'] || reference.challenge.de : '';
  const solutionText = reference.solution ? reference.solution[language as 'de' | 'en'] || reference.solution.de : '';
  const quoteText = reference.testimonial?.quote ? reference.testimonial.quote[language as 'de' | 'en'] || reference.testimonial.quote.de : '';
  
  // Hole den angepassten Button-Text oder verwende den Standard-Text
  const getButtonText = () => {
    if (reference.buttonText) {
      return reference.buttonText[language as 'de' | 'en'] || reference.buttonText.de;
    }
    return language === 'de' ? 'Zur Website' : 'Visit Website';
  };
  
  return (
    <div className="scroll-mt-24 mb-20 pb-16 border-b border-gray-200" id={`reference-${reference.id}`}>
      {/* Zentrierter Titelbereich */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <div className="h-20 w-40 mr-6 flex-shrink-0 flex items-center justify-center bg-white rounded-lg shadow-md overflow-hidden p-3">
            <img 
              src={logoPath} 
              alt={`${reference.companyName} Logo`} 
              className="max-w-full max-h-full object-contain"
            />
          </div>
          <div className="text-left">
            <h2 className="text-3xl font-bold text-gray-900">{reference.title}</h2>
            <p className="text-md text-gray-600">{reference.companyName}</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {reference.tags.map((tag, index) => (
                <span key={index} className="text-sm px-3 py-1 bg-blue-100 text-blue-800 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
        
        {/* Auszeichnung - wird nur für Eventbranchenverzeichnis angezeigt */}
        {reference.id === 'eventbranchenverzeichnis' && (
          <div className="mt-4 p-4 bg-orange-50 rounded-xl border border-orange-200 relative mx-auto w-[800px]">
            <div className="flex items-center">
              <div className="bg-orange-500 p-2 rounded-full mr-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8.21 13.89L7 23L12 20L17 23L15.79 13.88"></path>
                  <path d="M12 2L14.39 6.9L20.01 7.76L16.01 11.87L16.94 17.56L12 14.91L7.06 17.56L7.99 11.87L3.99 7.76L9.61 6.9L12 2Z"></path>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-orange-800">Ausgezeichnetes Portal</h3>
                <p className="text-orange-700">
                  Auszeichnung erhalten "Deutschlands Beste Online-Portale 2025"
                  <a href="https://disq.de/online-portale-preis.html" target="_blank" rel="noopener noreferrer" className="ml-2 underline">
                    Mehr erfahren
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Inhalt: Galerie und Text nebeneinander */}
      <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-16 items-start`}>
        <div className="lg:w-1/2">
          <Gallery
            projectId={reference.id}
            projectName={reference.companyName}
            images={reference.gallery || []}
            logoFallback={reference.logo}
          />
        </div>
        <div className="lg:w-1/2 space-y-6">          
          {challengeText && (
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-3">Die Herausforderung</h3>
              <p className="text-gray-700">{challengeText}</p>
            </div>
          )}
          
          {solutionText && (
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-3">Unsere Lösung</h3>
              <p className="text-gray-700">{solutionText}</p>
            </div>
          )}
          
          {!challengeText && !solutionText && descriptionText && (
            <div>
              <h3 className="text-lg font-semibold text-blue-700 mb-3">Beschreibung</h3>
              <p className="text-gray-700">{descriptionText}</p>
            </div>
          )}
          
          {/* Mehrere Buttons aus dem Buttons-Array */}
          {reference.buttons && reference.buttons.length > 0 && (
            <div className="flex flex-col sm:flex-row gap-3 mt-4 flex-wrap">
              {reference.buttons.map((button, index) => {
                // Button-Variante und Klassen basierend auf color-Attribut festlegen
                const getButtonVariant = () => {
                  if (!button.color) return "outline";
                  return "default";
                };
                
                const getButtonClass = () => {
                  switch(button.color) {
                    case 'blue': return "bg-blue-600 hover:bg-blue-700";
                    case 'green': return "bg-green-600 hover:bg-green-700";
                    case 'orange': return "bg-orange-600 hover:bg-orange-700";
                    case 'red': return "bg-red-600 hover:bg-red-700";
                    case 'purple': return "bg-purple-600 hover:bg-purple-700";
                    default: return "";
                  }
                };
                
                // Icon basierend auf icon-Attribut festlegen
                const renderIcon = () => {
                  switch(button.icon) {
                    case 'apple':
                      return (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M9 7c-3 0 -4 3 -4 5.5c0 3 2 7.5 4 7.5c1.088 -.046 1.679 -.5 3 -.5c1.312 0 1.5 .5 3 .5s4 -3 4 -5c-.028 -.01 -2.472 -.403 -2.5 -3c-.019 -2.17 2.416 -2.954 2.5 -3c-1.023 -1.492 -2.951 -1.963 -3.5 -2c-1.433 -.111 -2.83 1 -3.5 1c-.68 0 -1.9 -1 -3 -1z" />
                          <path d="M12 4a2 2 0 0 0 2 -2a2 2 0 0 0 -2 2" />
                        </svg>
                      );
                    case 'android':
                      return (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M4 3.71v16.58a.7 .7 0 0 0 1.05 .606l14.622 -8.42a.55 .55 0 0 0 0 -.953l-14.622 -8.419a.7 .7 0 0 0 -1.05 .607z" />
                          <path d="M15 9l-10.5 11.5" />
                          <path d="M4.5 3.5l10.5 11.5" />
                        </svg>
                      );
                    case 'website':
                    default:
                      return <ArrowRight size={16} className="mr-2" />;
                  }
                };
                
                return (
                  <Button 
                    key={index} 
                    asChild 
                    variant={getButtonVariant()} 
                    className={`${getButtonClass()}`}
                  >
                    <a href={button.url} target="_blank" rel="noopener noreferrer" className="flex items-center">
                      {renderIcon()}
                      {button.text[language as 'de' | 'en'] || button.text.de}
                    </a>
                  </Button>
                );
              })}
            </div>
          )}
          
          {/* Legacy Button für Projekte ohne button-Array */}
          {!reference.buttons && reference.website && (
            <Button asChild variant="outline" className="mt-4">
              <a href={reference.website} target="_blank" rel="noopener noreferrer" className="flex items-center">
                {getButtonText()} <ArrowRight size={16} className="ml-2" />
              </a>
            </Button>
          )}
        </div>
      </div>
      
      
      
      {/* Kundenstimme */}
      {quoteText && (
        <div className="mt-10 p-6 bg-gray-50 rounded-xl border border-gray-100 relative">
          <Quote size={40} className="text-blue-100 absolute -top-3 -left-3" />
          <blockquote className="pl-4 text-gray-700 italic text-lg">{quoteText}</blockquote>
          <div className="mt-4 flex items-center">
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{reference.testimonial?.personName}</p>
              {reference.testimonial?.personTitle && (
                <p className="text-sm text-gray-600">
                  {reference.testimonial.personTitle}
                  {reference.testimonial.companyName && `, ${reference.testimonial.companyName}`}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferenceCard;
